(ns app.front.view.profile.change-password
  (:require [app.front.state-machine :as sm]
            [app.front.ui.password :as ui-password]
            [app.front.validate :as validate :refer [field-invalid-text field-invalid?]]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [goog.functions]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))


(def NonBlankString (s/pred (complement str/blank?) 'string-cannot-be-blank))
(def PasswordScore (s/pred #(contains? #{:good :moderate} %) 'weak-password))


(def error-messages {:user/current-password [[NonBlankString :error/check-password]]
                     :user/new-password     [[NonBlankString :error/check-new-password]]
                     ::password-new-score   [[PasswordScore :validate/password-score]]})


(def initial-document {:user/current-password ""
                       :user/new-password     ""
                       ::password-new-score   nil

                       ::state         nil
                       ::show-errors?  false
                       ::errors        {}
                       ::server-errors nil})


(def state-machine
  {nil                 {::init ::ready}
   ::ready             {::set-doc-value ::client-validation
                        ::try-submit    ::submitting}
   ::client-validation {::no-errors    ::ready
                        ::still-errors ::client-validation}
   ::submitting        {::submit-errors  ::server-errors
                        ::submit-success ::ready}
   ::server-errors     {::set-doc-value ::client-validation}})


;; -- Subscriptions -----------------------------------------------------------

(rf/reg-sub ::document
  (fn [db [_ path]]
    (let [doc (::document db)]
      (if path (get-in doc path) doc))))


(rf/reg-sub ::state
  :<- [::document]
  (fn [document _] (get document ::state)))


(rf/reg-sub ::show-errors?
  :<- [::document]
  (fn [document _] (get document ::show-errors?)))


(rf/reg-sub ::server-errors
  :<- [::document]
  (fn [document _] (get document ::server-errors)))


(rf/reg-sub ::submit-disabled?
  :<- [::state]
  :<- [::document]
  (fn [[state document] _]
    (or (not (empty? (validate/validate-form document error-messages)))
        (= state ::submitting))))


;; -- Events ------------------------------------------------------------------

(def update-next-state (sm/make-update-next-state state-machine [::document ::state]))

(def debug (rf/after (fn [db event]
                       (.log js/console "=======")
                       (.log js/console "state: " (str (-> db ::document ::state)))
                       (.log js/console "event: " (str event)))))


(def interceptors [(rf/inject-cofx :tr) (when ^boolean js/goog.DEBUG debug)])


(defn handle-init
  [db [event _]]
  (-> db
      (assoc ::document initial-document)
      (update-next-state event)))


(defn handle-set-doc-value
  [{:keys [db]} [event path value]]
  {:db       (-> db
                 (assoc-in (into [::document] path) value)
                 (update ::document dissoc ::server-errors)
                 (update-next-state event))
   :dispatch [::validate-fields]})


(defn handle-submit-clicked
  [{:keys [db]} _]
  {:db       (assoc-in db [::document ::show-errors?] true)
   :dispatch (if-not (empty? (validate/validate-form (::document db) error-messages))
               [::validate-fields]
               [::try-submit])})


(defn handle-validate-fields
  [{:keys [db]} _]
  {:dispatch (if (empty? (validate/validate-form (::document db) error-messages))
               [::no-errors]
               [::still-errors])})


(rc/reg-chain-named*
  interceptors
  ::try-submit
  (fn [{:keys [db]} [event _]]
    {:db           (update-next-state db event)
     :comm/command {:command :user/change-password
                    :data    (-> (::document db)
                                 (select-keys [:user/current-password
                                               :user/new-password]))

                    :on-bad-request [::submit-failure]}})
  ::submit-success
  (fn [{:keys [tr db]} [_]]
    {:db       (-> db
                   (assoc ::document initial-document)
                   (update-next-state ::init))
     :dispatch [:notifications/add
                :toast
                :success
                (tr [:user/changes-saved])
                (tr [:user/password-changed])]}))


(rc/reg-chain* ::submit-failure
  interceptors
  (fn [{:keys [db]} [_ status body]]
    (js/console.error "::submit-failure" status body)
    (if (= 400 status)
      {:db       (assoc-in db
                           [::document ::server-errors]
                           (:errors body))
       :dispatch [::submit-errors]}
      {:dispatch [::submit-errors]})))


(rf/reg-event-db ::init interceptors handle-init)

(rf/reg-event-fx ::set-doc-value interceptors handle-set-doc-value)
(rf/reg-event-fx ::validate-fields handle-validate-fields)
(rf/reg-event-fx ::submit-clicked interceptors handle-submit-clicked)

(rf/reg-event-db ::no-errors update-next-state)
(rf/reg-event-db ::still-errors update-next-state)

(rf/reg-event-db ::submit-errors interceptors update-next-state)


(defn change-password-form [tr validator]
  [:form.flex.flex-col.w-full
   {:on-submit   (fn [e] (.preventDefault e) (>evt [::submit-clicked]))
    :no-validate true}
   [:fieldset
    [:legend.mb-6.font-semibold.text-base (tr [:user/change-password])]
    [:div.flex.flex-wrap
     [:div {:class "mt-3 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 w-full md:w-1/2 lg:w-1/2 xl:w-1/4"}
      [ui-password/password-input
       {:tr            tr
        :id            "input-current-password"
        :label         (tr [:user/current-password])
        :auto-complete "current-password"
        :value         (or (<sub [::document [:user/current-password]]) "")
        :handle-change (fn [password]
                         (rf/dispatch-sync [::set-doc-value [:user/current-password] password]))
        :invalid?      (field-invalid? (<sub [::show-errors?]) validator :user/current-password)
        :invalid-text  (field-invalid-text (<sub [::show-errors?]) validator :user/current-password tr)
        :use-meter?    false}]]
     [:div {:class "mt-3 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 w-full md:w-1/2 lg:w-1/2 xl:w-1/4"}
      [ui-password/password-input
       {:tr              tr
        :id              "input-new-password"
        :label           (tr [:user/new-password])
        :auto-complete   "new-password"
        :value           (or (<sub [::document [:user/new-password]]) "")
        :handle-change   (fn [password]
                           (rf/dispatch-sync [::set-doc-value [:user/new-password] password]))
        :on-score-change #(>evt [::set-doc-value [::password-new-score] %])
        :invalid?        (field-invalid? (<sub [::show-errors?]) validator :user/new-password)
        :invalid-text    (field-invalid-text (<sub [::show-errors?]) validator :user/new-password tr)
        :use-meter?      true}]]
     [:div {:class "mt-2 xl:mt-8 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 mr-auto"
            :style {:padding-top "4px"}}
      (cc/button
        {:type             :submit
         :size             :field
         :disabled         (<sub [::submit-disabled?])
         :icon             i/rotate-right
         :icon-description (tr [:user/change-password])}
        (tr [:user/change-password]))]]]])


(defn view []
  (rf/dispatch [::init])
  (let [tr        (<sub [:tr])
        validator #(or (get (<sub [::server-errors]) %)
                       (validate/validate-field % (<sub [::document]) error-messages))]
    (fn []
      [:div.flex.flex-1.items-center.justify-center
       [change-password-form tr validator]])))
